<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      persistent
      scrollable
      width="auto"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          color="primary"
          v-bind="props"
          @click="dialogComputed = true"
        >
        Get Files from SFTP
        </v-btn>
      </template>
      <v-card>
        <v-card-title>Select Files</v-card-title>
        <v-divider></v-divider>
        <v-row class="spacedRow">
      <v-col>
        <v-progress-linear indeterminate v-if="isLoadingData">
        </v-progress-linear>
      </v-col>
    </v-row>
        <v-card-text style="height: 30rem;">
          <!-- Why the list is empty, rather than an empty dialog that looks the same
               whether the server is unreachable or the folder holds nothing. -->
          <div v-if="!isLoadingData && !fileList.length" class="pa-2">
            {{ listMessage }}
          </div>
          <!-- TICKET-2778: one source (FTP or the Clerk system) can be down while the
               other still lists; say which, above the files that did come back. -->
          <v-alert
            v-for="warning in listWarnings"
            :key="warning"
            type="warning"
            dense
            text
            class="mb-2"
          >{{ warning }}</v-alert>
          <v-radio-group
            v-model="selectedFile"
            column
          >
            <!-- v-for sits on the v-radio itself: the <template> wrapped a single
                 element and bought nothing, while splitting the loop from its key is
                 what the editor flags (its rule is the Vue 3 one - this is Vue 2, where
                 a key on <template v-for> is invalid, so moving it up is not the fix). -->
            <v-radio
              v-for="entry in fileList"
              :key="entry.value"
              :label="entry.label"
              :value="entry.value"
            ></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="blue-darken-1"
            variant="text"
            @click="dialog = false"
          >
            Close
          </v-btn>
          <v-btn
            color="blue-darken-1"
            variant="text"
            @click="previewFile"
          >
            Preview
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <TransformMapperPreviewData
      ref="childComponentRef"
      :isImportedData="true"
      @importDataFromFile="importDataFromFile"
    />
  </v-row>
</template>

<script>
import { mapActions } from 'vuex';
import TransformMapperPreviewData from '@/components/config/TransformMapperPreviewImport.vue';
import { clients } from '@/util/clients/index';
import {
  clerkBatchIdFromPath,
  toPickerEntry,
} from '@/util/shared/treasurer-link';
import {
  ftpEmptyMessage,
  ftpErrorMessage,
  readFtpFileList,
  readFtpPath,
} from '@/util/shared/ftp-file-list';

const { backendRest } = clients.direct;

export default {
  components: {
    TransformMapperPreviewData,
  },
  props: {
    pipeline: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    selectedFile: undefined,
    dialog: false,
    fileList: [],
    fileData: {},
    isLoadingData: true,
    // Why fileList is empty - a connection error or the folder that was read.
    listMessage: '',
    // FTP / Clerk system failures that did not stop the other source from listing
    listWarnings: [],
  }),
  created() {
  },
  computed: {
    dialogComputed: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.dialog = value;
        this.fileList = [];
        this.getListFromSftp();
      },
    },
  },
  methods: {
    ...mapActions([
      'flashError',
    ]),
    async getListFromSftp() {
      let response = {};
      const { sftpType } = this.pipeline.customConfig.importProfile;
      this.isLoadingData = true;
      this.listMessage = '';
      this.listWarnings = [];
      try {
        if (sftpType == null) {
          this.listMessage = 'This import profile has no SFTP connection configured.';
          this.fileList = [];
          return;
        }
        response = await backendRest.get(`/sftp/get-import-list?sftpType=${sftpType}`);
        // Clerk system batches (TICKET-2778) come first, with a label of their own
        this.fileList = readFtpFileList(response.data).map(toPickerEntry);
        const data = response.data || {};
        this.listWarnings = [data.clerkError, data.sftpError].filter(Boolean);
        if (!this.fileList.length) {
          this.listMessage = ftpEmptyMessage(readFtpPath(response.data));
        }
      } catch (e) {
        console.error(e);
        // The reason the SFTP server gave, not "facing issue" - see TKT-000856.
        this.fileList = [];
        this.listMessage = ftpErrorMessage(e);
        this.flashError(this.listMessage);
      } finally {
        this.isLoadingData = false;
      }
    },
    async previewFile() {
      let response = {};
      this.isLoadingData = true;
      const { sftpType } = this.pipeline.customConfig.importProfile;
      if (this.selectedFile && sftpType != null) {
        try {
          response = await backendRest.get(`/sftp/download-file?filePath=${encodeURIComponent(this.selectedFile)}&sftpType=${sftpType}`);
        } catch (e) {
          console.error(e);
          const reason = ((e && e.response && e.response.data) || {}).error;
          this.flashError(reason || 'Facing issue while downloading file from SFTP');
        }
      }
      this.fileData = response.data || {};
      this.$refs.childComponentRef.previewImportedData(this.pipeline, this.fileData.parsedData || '');
      this.isLoadingData = false;
    },
    importDataFromFile() {
      this.dialog = false;
      const entry = this.fileList.find((f) => f.value === this.selectedFile) || {};
      const file = new File([this.fileData.parsedData || ''], this.fileData.fileName || entry.fileName || this.selectedFile);
      // A released Clerk batch: the import tells the Clerk system once it has succeeded.
      const batchId = clerkBatchIdFromPath(this.selectedFile);
      if (batchId) {
        file.treasurerLinkBatchId = batchId;
      }
      this.$emit('importDataFromFile', file);
    },
  },
};
</script>
